import { useState, useEffect } from "react";
import { useTheme } from "../../../../contexts/ThemeContext";
import { createBackgroundStyles } from "../../../../styles/masterStyles";
import {
  heritageDescriptions,
  innateHeritages,
} from "../../../../SharedData/heritageData";
import {
  checkForSkillProficiencies,
  applyHeritageProficiencies,
  removeHeritageProficiencies,
} from "../../utils/utils";

const checkForAbilityChoices = (item) => {
  const abilities = [];

  if (item.abilityScoreIncrease) {
    abilities.push(...item.abilityScoreIncrease);
  }

  if (item.data?.abilityScoreIncrease) {
    abilities.push(...item.data.abilityScoreIncrease);
  }

  if (item.properties?.abilityScoreIncrease) {
    abilities.push(...item.properties.abilityScoreIncrease);
  }

  return abilities;
};

const formatHeritageBenefits = (heritageData) => {
  if (!heritageData) return [];

  const benefits = [];

  if (heritageData.benefits && Array.isArray(heritageData.benefits)) {
    benefits.push(...heritageData.benefits);
  }

  if (heritageData.modifiers?.skillProficiencies?.length > 0) {
    const skillText = `Skill Proficiencies: ${heritageData.modifiers.skillProficiencies.join(
      ", "
    )}`;
    if (
      !benefits.some((benefit) =>
        benefit.toLowerCase().includes("skill proficienc")
      )
    ) {
      benefits.push(skillText);
    }
  }

  return benefits;
};

const HeritageChoiceSelector = ({
  heritage,
  heritageData,
  heritageChoices,
  onHeritageChoiceSelect,
  isSelected,
  styles,
  theme,
}) => {
  if (!heritage || !heritageData?.features || !isSelected) return null;

  const choiceFeatures = heritageData.features.filter(
    (feature) => feature.isChoice
  );

  if (choiceFeatures.length === 0) return null;

  const handleChoiceChange = (featureName, choiceName, feature) => {
    if (feature.isMultiSelect) {
      const currentSelections = heritageChoices[heritage]?.[featureName] || [];
      const newSelections = currentSelections.includes(choiceName)
        ? currentSelections.filter((selection) => selection !== choiceName)
        : [...currentSelections, choiceName];
      onHeritageChoiceSelect(heritage, featureName, newSelections);
    } else {
      onHeritageChoiceSelect(heritage, featureName, choiceName);
    }
  };

  const isChoiceSelected = (featureName, choiceName, feature) => {
    if (feature.isMultiSelect) {
      const selections = heritageChoices[heritage]?.[featureName] || [];
      return selections.includes(choiceName);
    }
    return heritageChoices[heritage]?.[featureName] === choiceName;
  };

  return (
    <div style={{ marginTop: "12px" }}>
      {choiceFeatures.map((feature, featureIndex) => {
        const currentChoice = heritageChoices[heritage]?.[feature.name];

        return (
          <div key={featureIndex} style={styles.heritageChoiceContainer}>
            <div style={styles.heritageChoiceLabel}>{feature.description}:</div>

            {feature.options.map((option, optionIndex) => {
              const isOptionSelected = isChoiceSelected(
                feature.name,
                option.name,
                feature
              );
              return (
                <div key={optionIndex} style={styles.choiceOption}>
                  <label style={styles.choiceLabel}>
                    <input
                      type={feature.isMultiSelect ? "checkbox" : "radio"}
                      name={
                        feature.isMultiSelect
                          ? `${heritage}_${feature.name}_${option.name}`
                          : `${heritage}_${feature.name}`
                      }
                      value={option.name}
                      checked={isOptionSelected}
                      onChange={(e) =>
                        handleChoiceChange(
                          feature.name,
                          e.target.value,
                          feature
                        )
                      }
                      style={styles.choiceRadio}
                    />
                    <div style={styles.choiceContent}>
                      <div style={styles.choiceName}>{option.name}</div>
                      <div style={styles.choiceDescription}>
                        {option.description}
                      </div>

                      {checkForSkillProficiencies(option).length > 0 && (
                        <div style={styles.choiceBenefits}>
                          <strong>Skills:</strong>{" "}
                          {checkForSkillProficiencies(option).join(", ")}
                        </div>
                      )}
                      {(option.toolProficiencies ||
                        option.data?.toolProficiencies ||
                        option.properties?.toolProficiencies) && (
                        <div style={styles.choiceBenefits}>
                          <strong>Tools:</strong>{" "}
                          {(
                            option.toolProficiencies ||
                            option.data?.toolProficiencies ||
                            option.properties?.toolProficiencies ||
                            []
                          ).join(", ")}
                        </div>
                      )}
                      {checkForAbilityChoices(option).length > 0 && (
                        <div style={styles.choiceBenefits}>
                          <strong>Ability:</strong>{" "}
                          {checkForAbilityChoices(option).map((choice, idx) => (
                            <span key={idx}>
                              +{choice.amount}{" "}
                              {choice.ability.charAt(0).toUpperCase() +
                                choice.ability.slice(1)}
                              {idx < checkForAbilityChoices(option).length - 1
                                ? ", "
                                : ""}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

const InnateHeritageSection = ({
  character,
  handleInputChange,
  isEditing = false,
  heritageChoices = {},
  onHeritageChoicesChange,
  onCharacterUpdate,
  disabled = false,
}) => {
  const { theme } = useTheme();
  const styles = createBackgroundStyles(theme);
  const [expandedHeritages, setExpandedHeritages] = useState(new Set());
  const [heritageFilter, setHeritageFilter] = useState("");

  const hasSelectedHeritage = character.innateHeritage ? 1 : 0;

  const enhancedStyles = {
    ...styles,
    modifierPillsContainer: {
      backgroundColor: "rgba(139, 92, 246, 0.1)",
      border: "1px solid rgba(139, 92, 246, 0.3)",
      borderRadius: "8px",
      padding: "12px",
      marginBottom: "16px",
    },
    pillsLabel: {
      fontSize: "12px",
      fontWeight: "600",
      color: "#8b5cf6",
      marginBottom: "8px",
    },
    pillsRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: "8px",
    },
    modifierPill: {
      display: "flex",
      alignItems: "center",
      gap: "4px",
      backgroundColor: "#8b5cf6",
      color: "white",
      padding: "4px 8px",
      borderRadius: "12px",
      fontSize: "11px",
      fontWeight: "600",
      cursor: "help",
    },
    pillAbility: {
      opacity: 0.9,
    },
    pillModifier: {
      fontWeight: "bold",
    },
    heritageChoiceContainer: {
      backgroundColor: theme.surfaceHover || theme.surface,
      border: `1px solid ${theme.border}`,
      borderRadius: "6px",
      padding: "12px",
      marginBottom: "12px",
    },
    heritageChoiceLabel: {
      fontSize: "12px",
      fontWeight: "600",
      color: theme.text,
      marginBottom: "8px",
    },
    choiceOption: {
      marginBottom: "8px",
    },
    choiceLabel: {
      display: "flex",
      cursor: "pointer",
    },
    choiceContent: {
      alignItems: "flex-start",
      gap: "8px",
      padding: "8px",
      borderRadius: "4px",
      transition: "all 0.2s ease",
    },
    choiceName: {
      fontSize: "12px",
      fontWeight: "600",
      color: theme.text,
      marginBottom: "2px",
    },
    choiceDescription: {
      fontSize: "11px",
      color: theme.textSecondary,
      lineHeight: "1.3",
      marginBottom: "4px",
    },
    choiceBenefits: {
      fontSize: "10px",
      color: "#8b5cf6",
      marginTop: "2px",
    },
    choiceRadio: {
      marginTop: "2px",
      accentColor: "#8b5cf6",
    },
    choiceRequired: {
      fontSize: "11px",
      color: theme.warning || "#f59e0b",
      fontStyle: "italic",
      marginTop: "4px",
    },
    warningBadge: styles.warningBadge || {
      backgroundColor: "#fef3c7",
      color: "#92400e",
      padding: "4px 8px",
      borderRadius: "4px",
      fontSize: "11px",
      fontWeight: "600",
    },
    levelRestrictionWarning: styles.levelRestrictionWarning || {
      backgroundColor: "#fef3c7",
      border: "1px solid #fbbf24",
      borderRadius: "8px",
      padding: "12px",
      marginBottom: "16px",
      color: "#92400e",
    },
    modalOverlay: styles.modalOverlay || {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
    },
    modalContent: styles.modalContent || {
      backgroundColor: theme.surface,
      padding: "24px",
      borderRadius: "12px",
      maxWidth: "500px",
      width: "90%",
      maxHeight: "80vh",
      overflow: "auto",
    },
    cancelButton: styles.cancelButton || {
      padding: "8px 16px",
      border: `1px solid ${theme.border}`,
      borderRadius: "6px",
      backgroundColor: theme.surface,
      color: theme.text,
      cursor: "pointer",
    },
    confirmButton: styles.confirmButton || {
      padding: "8px 16px",
      border: "none",
      borderRadius: "6px",
      backgroundColor: theme.warning || "#f59e0b",
      color: "white",
      cursor: "pointer",
    },
    featFilterContainer: styles.featFilterContainer || {
      position: "relative",
      marginBottom: "16px",
    },
    featFilterInput: styles.featFilterInput || {
      width: "100%",
      padding: "12px 40px 12px 16px",
      fontSize: "14px",
      border: `2px solid ${theme.border}`,
      borderRadius: "8px",
      backgroundColor: theme.surface,
      color: theme.text,
      outline: "none",
    },
    featFilterClearButton: styles.featFilterClearButton || {
      position: "absolute",
      right: "12px",
      top: "50%",
      transform: "translateY(-50%)",
      background: "none",
      border: "none",
      fontSize: "18px",
      color: theme.textSecondary,
      cursor: "pointer",
      padding: "4px",
      borderRadius: "4px",
    },
  };

  useEffect(() => {
    if (character.innateHeritage && character.innateHeritage.trim() !== "") {
      setExpandedHeritages((prev) => {
        const newSet = new Set(prev);
        newSet.add(character.innateHeritage);
        return newSet;
      });
    }
  }, [character.innateHeritage]);

  const toggleHeritageExpansion = (heritageName) => {
    setExpandedHeritages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(heritageName)) {
        newSet.delete(heritageName);
      } else {
        newSet.add(heritageName);
      }
      return newSet;
    });
  };

  const getFilteredHeritages = () => {
    // If a heritage is selected, only show that one
    if (character.innateHeritage) {
      return [character.innateHeritage];
    }

    // Otherwise, apply search filter or show all
    if (!heritageFilter.trim()) return innateHeritages;

    const searchTerm = heritageFilter.toLowerCase();
    return innateHeritages.filter((heritage) => {
      const heritageData = heritageDescriptions[heritage];
      return (
        heritage.toLowerCase().includes(searchTerm) ||
        heritageData?.description?.toLowerCase().includes(searchTerm)
      );
    });
  };

  const handleHeritageToggle = (heritageName) => {
    const newValue =
      character.innateHeritage === heritageName ? "" : heritageName;

    const updatedCharacter = applyHeritageProficiencies(
      character,
      newValue,
      heritageChoices
    );

    // Use onCharacterUpdate to update the entire character at once
    if (onCharacterUpdate) {
      onCharacterUpdate(updatedCharacter);
    } else {
      // Fallback to individual updates if onCharacterUpdate isn't available
      handleInputChange("innateHeritage", updatedCharacter.innateHeritage);
      handleInputChange(
        "skillProficiencies",
        updatedCharacter.skillProficiencies
      );
      handleInputChange(
        "innateHeritageSkills",
        updatedCharacter.innateHeritageSkills
      );
    }

    if (!newValue && onHeritageChoicesChange) {
      const newChoices = { ...heritageChoices };
      delete newChoices[character.innateHeritage];
      onHeritageChoicesChange(newChoices);
    }
  };

  const handleHeritageChoiceSelect = (heritage, featureName, choiceName) => {
    const newChoices = {
      ...heritageChoices,
      [heritage]: {
        ...heritageChoices[heritage],
        [featureName]: choiceName,
      },
    };

    onHeritageChoicesChange(newChoices);

    const updatedCharacter = applyHeritageProficiencies(
      character,
      heritage,
      newChoices
    );
    handleInputChange(
      "skillProficiencies",
      updatedCharacter.skillProficiencies
    );
    handleInputChange(
      "innateHeritageSkills",
      updatedCharacter.innateHeritageSkills
    );
  };

  const getRequiredChoices = (heritageData) => {
    if (!heritageData?.features) {
      return { total: 0, missing: [], isComplete: true };
    }

    const choiceFeatures = heritageData.features.filter(
      (feature) => feature.isChoice
    );
    const missingChoices = choiceFeatures.filter((feature) => {
      const choice = heritageChoices[character.innateHeritage]?.[feature.name];
      if (feature.isMultiSelect) {
        return false;
      }
      return !choice;
    });

    return {
      total: choiceFeatures.length,
      missing: missingChoices.map((f) => f.name),
      isComplete: missingChoices.length === 0,
    };
  };

  const filteredHeritages = getFilteredHeritages();
  const selectedHeritageData = character.innateHeritage
    ? heritageDescriptions[character.innateHeritage]
    : null;
  const choiceStatus = selectedHeritageData
    ? getRequiredChoices(selectedHeritageData)
    : { total: 0, missing: [], isComplete: true };

  return (
    <div style={styles.container}>
      <div
        style={{
          maxHeight: "800px",
          overflowY: "auto",
          overflowX: "hidden",
          paddingRight: "4px",
        }}
      >
        <div style={enhancedStyles.featFilterContainer}>
          <input
            type="text"
            placeholder="Search heritages by name or description..."
            value={heritageFilter}
            onChange={(e) => setHeritageFilter(e.target.value)}
            style={enhancedStyles.featFilterInput}
            disabled={disabled}
          />
          {heritageFilter.trim() && (
            <button
              onClick={() => setHeritageFilter("")}
              style={enhancedStyles.featFilterClearButton}
              type="button"
              title="Clear search"
              disabled={disabled}
            >
              ×
            </button>
          )}
        </div>

        <div style={enhancedStyles.availableElementsContainer}>
          {filteredHeritages.map((heritage) => {
            const heritageData = heritageDescriptions[heritage];
            const isSelected = character.innateHeritage === heritage;
            const isExpanded = expandedHeritages.has(heritage);

            return (
              <div
                key={heritage}
                style={
                  isSelected
                    ? enhancedStyles.selectedElementCard
                    : enhancedStyles.featCard
                }
              >
                <div style={enhancedStyles.featHeader}>
                  <label
                    style={enhancedStyles.featLabelClickable}
                    onClick={(e) => {
                      // Prevent double-firing when clicking the checkbox itself
                      if (e.target.tagName === "INPUT") return;
                      handleHeritageToggle(heritage);
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleHeritageToggle(heritage);
                      }}
                      disabled={disabled}
                      style={{
                        width: "18px",
                        height: "18px",
                        marginRight: "8px",
                        cursor: disabled ? "not-allowed" : "pointer",
                        accentColor: isSelected ? theme.success : theme.primary,
                        transform: "scale(1.2)",
                      }}
                    />
                    <span
                      style={
                        isSelected
                          ? enhancedStyles.featNameSelected
                          : enhancedStyles.featName
                      }
                    >
                      {heritage}
                    </span>
                  </label>
                  <button
                    onClick={() => toggleHeritageExpansion(heritage)}
                    style={enhancedStyles.expandButton}
                    type="button"
                    disabled={disabled}
                  >
                    {isExpanded ? "▲" : "▼"}
                  </button>
                </div>

                <div
                  style={
                    isSelected
                      ? enhancedStyles.featPreviewSelected
                      : enhancedStyles.featPreview
                  }
                >
                  <div style={{ marginBottom: "8px" }}>
                    {heritageData?.description || "Heritage description"}
                  </div>

                  {formatHeritageBenefits(heritageData).length > 0 && (
                    <div
                      style={{
                        backgroundColor: theme.background,
                        border: `1px solid ${theme.border}`,
                        borderRadius: "6px",
                        padding: "8px",
                        marginTop: "8px",
                      }}
                    >
                      <strong
                        style={{
                          fontSize: "12px",
                          color: theme.primary,
                          display: "block",
                          marginBottom: "6px",
                        }}
                      >
                        Benefits:
                      </strong>
                      <ul
                        style={{
                          margin: "0",
                          paddingLeft: "16px",
                          fontSize: "13px",
                          lineHeight: "1.4",
                        }}
                      >
                        {formatHeritageBenefits(heritageData).map(
                          (benefit, idx) => (
                            <li
                              key={idx}
                              style={{
                                marginBottom: "4px",
                                color: theme.text,
                                fontStyle: "normal",
                              }}
                            >
                              {benefit}
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  )}
                </div>

                {isExpanded && (
                  <div style={enhancedStyles.featDescription}>
                    <div style={{ marginBottom: "16px" }}>
                      <p
                        style={{
                          fontSize: "14px",
                          lineHeight: "1.5",
                          margin: "0 0 12px 0",
                        }}
                      >
                        {heritageData?.description || "Heritage description"}
                      </p>
                    </div>

                    {heritageData?.features &&
                      heritageData.features.length > 0 && (
                        <div>
                          <strong
                            style={{ fontSize: "12px", color: theme.primary }}
                          >
                            Features:
                          </strong>
                          {heritageData.features
                            .slice(0, 2)
                            .map((feature, idx) => (
                              <div key={idx} style={{ marginTop: "6px" }}>
                                <div
                                  style={{
                                    fontSize: "11px",
                                    fontWeight: "600",
                                  }}
                                >
                                  {feature.name}
                                </div>
                                <div
                                  style={{
                                    fontSize: "11px",
                                    color: theme.textSecondary,
                                    lineHeight: "1.3",
                                  }}
                                >
                                  {feature.description?.substring(0, 100)}
                                  {feature.description?.length > 100
                                    ? "..."
                                    : ""}
                                </div>
                              </div>
                            ))}
                          {heritageData.features.length > 2 && (
                            <div
                              style={{
                                fontSize: "11px",
                                color: theme.textSecondary,
                                fontStyle: "italic",
                                marginTop: "4px",
                              }}
                            >
                              +{heritageData.features.length - 2} more feature
                              {heritageData.features.length > 3 ? "s" : ""}
                            </div>
                          )}
                        </div>
                      )}
                  </div>
                )}

                <HeritageChoiceSelector
                  heritage={heritage}
                  heritageData={heritageData}
                  heritageChoices={heritageChoices}
                  onHeritageChoiceSelect={handleHeritageChoiceSelect}
                  isSelected={isSelected}
                  styles={enhancedStyles}
                  theme={theme}
                />
              </div>
            );
          })}
        </div>

        <div style={enhancedStyles.helpText}>
          {character.innateHeritage
            ? "Your selected heritage provides unique magical abilities and characteristics. You can change this at any time."
            : "Choose a heritage that represents your character's species and inherent abilities. This is optional - you may leave it blank if desired."}
        </div>
      </div>
    </div>
  );
};

export default InnateHeritageSection;
